import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { WantedLevelService } from './WantedLevelService';

const ESCAPE_DURATION_BY_LEVEL: Record<number, number> = {
    1: 30,
    2: 30,
    3: 45,
    4: 60,
    5: 90,
};

@Injectable({ providedIn: 'root' })
export class EscapeService {
    readonly escapeCountdown$ = new BehaviorSubject<number | null>(null);
    readonly escaped$ = new Subject<void>();
    readonly anyPursuerHasLoS$ = new BehaviorSubject<boolean>(false);

    private countdown: number | null = null;
    private requiredDuration = 0;
    private requiresInitialSpotting = false;

    constructor(private readonly wantedLevelService: WantedLevelService) {
        let previousLevel = 0;
        this.wantedLevelService.wantedLevel$.subscribe(level => {
            if (level > previousLevel) {
                this.requiresInitialSpotting = true;
                this.resetCountdown();
            }
            previousLevel = level;
        });
    }

    tick(deltaTime: number, anyPursuerHasLoS: boolean): void {
        if (anyPursuerHasLoS !== this.anyPursuerHasLoS$.value) {
            this.anyPursuerHasLoS$.next(anyPursuerHasLoS);
        }

        const level = this.wantedLevelService.wantedLevel;

        if (level === 0) { this.resetCountdown(); return; }

        if (anyPursuerHasLoS) {
            this.requiresInitialSpotting = false;
            this.resetCountdown();
            return;
        }

        if (this.requiresInitialSpotting) { return; }

        if (this.countdown === null) {
            this.requiredDuration = ESCAPE_DURATION_BY_LEVEL[level] ?? 30;
            this.countdown = this.requiredDuration;
        } else {
            const newRequired = ESCAPE_DURATION_BY_LEVEL[level] ?? 30;
            if (newRequired > this.requiredDuration) {
                const elapsed = this.requiredDuration - this.countdown;
                this.requiredDuration = newRequired;
                this.countdown = newRequired - elapsed;
            }
        }

        this.countdown -= deltaTime;
        this.escapeCountdown$.next(Math.max(0, this.countdown));

        if (this.countdown <= 0) {
            this.triggerEscape();
        }
    }

    private resetCountdown(): void {
        if (this.countdown !== null) {
            this.countdown = null;
            this.escapeCountdown$.next(null);
        }
    }

    private triggerEscape(): void {
        this.countdown = null;
        this.escapeCountdown$.next(null);
        this.escaped$.next();
    }
}
